import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API_URL from "../../config/ApiUrl";
import axios from "axios";
import { setAxiosInterceptor, clearFailedQueue } from "./interceptorSlice";
import { setAxiosHeaders, setAxiosBaseUrl } from "./apiService";
import { CONSTANT } from "../../config/Constant";
import { getLocalStorageData, setLocalStorageData } from "../../config/Global";
import ADMIN_API_URL from "../../config/AdminApiUrl";
import { ensureStarted as startRealtime } from "../../services/realtime/signalRConnection";
import {
  getTenantPackingSettings,
  getTenantSettings,
} from "../profile/profileSlice";

export const initializeApp = createAsyncThunk(
  "auth/initializeApp",
  async (_, { dispatch, getState }) => {
    setAxiosBaseUrl();
    setAxiosInterceptor(dispatch, getState);
    await dispatch(setHeaders());
    let token = getState().auth.token;
    const isa = localStorage.getItem("isa");

    // If token validation failed (expired), try silent refresh before showing login
    if (!token) {
      const expiredToken = localStorage.getItem("token");
      const storedRefreshToken = localStorage.getItem("refreshToken");

      if (expiredToken && storedRefreshToken) {
        try {
          const apiUrl =
            isa === "true"
              ? ADMIN_API_URL.VERIFICATION.REFRESH_TOKEN
              : API_URL.VERIFICATION.REFRESH_TOKEN;
          const { data } = await axios.post(apiUrl, {
            token: expiredToken,
            refreshToken: storedRefreshToken,
          });

          // Reset stale 401 flags BEFORE setHeaders
          // (setHeaders dispatches setAppReady(true) which mounts MainLayout —
          //  MainLayout must see clean flags or it will trigger an unnecessary refreshTokenApi)
          dispatch(setRefreshToken(data.refreshToken));
          dispatch(setIsUnauthorise(false));
          dispatch(setIsRefreshingToken(false));
          clearFailedQueue();
          await dispatch(setHeaders(data.token)).unwrap();
          token = getState().auth.token;
        } catch (refreshError) {
          // Clear stale tokens so login page starts clean
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
        }
      }

      // Reset any stale 401 flags (covers the case where no silent refresh was attempted)
      dispatch(setIsUnauthorise(false));
      dispatch(setIsRefreshingToken(false));
      clearFailedQueue();
    }

    if (token) {
      dispatch(getTenantPackingSettings());
      if (isa !== "true") {
        dispatch(getTenantSettings());
      }
      // Start the shared real-time (SignalR) connection once authenticated (best-effort —
      // features also start it lazily and fall back to polling if it can't connect).
      startRealtime().catch(() => {});
    }
    dispatch(setAppReady(true));
  },
);

export const manageLocalStorage = createAsyncThunk(
  "auth/manageLocalStorage",
  async (_, { dispatch }) => {
    const userId = localStorage.getItem("userId");
    const backOfficerId = localStorage.getItem("backOfficerId");
    const isa = localStorage.getItem("isa");
    dispatch(setUserId(userId));
    dispatch(setBackOfficerId(backOfficerId));
    dispatch(setIsa(isa));
  },
);

export const setHeaders = createAsyncThunk(
  "auth/setHeaders",
  async (access_token, { dispatch, getState, rejectWithValue }) => {
    try {
      const token = await setAxiosHeaders(access_token);
      dispatch(setToken(token));
      dispatch(manageLocalStorage());
      dispatch(setModuleRightData(getLocalStorageData("moduleRights")));
      const { backOfficerId, isa } = getState().auth;

      if (backOfficerId) {
        let payload;
        if (isa === "true") {
          payload = { id: backOfficerId };
        } else {
          payload = {
            entityId: backOfficerId,
            userType: CONSTANT.USER_TYPE.BACK_OFFICE,
          };
        }
        await dispatch(getUserProfileData(payload)).unwrap();
      }
      dispatch(setAppReady(true));
      return true;
    } catch (error) {
      dispatch(setToken());
      return rejectWithValue(error);
    }
  },
);

export const doLogin = createAsyncThunk(
  "auth/doLogin",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setAppReady(false));
      // Reset stale auth flags and queued requests from any previous 401
      // (e.g. expired token during initializeApp page reload)
      dispatch(setIsUnauthorise(false));
      dispatch(setIsRefreshingToken(false));
      clearFailedQueue();
      const { data } = await axios.post(API_URL.VERIFICATION.LOGIN, payload);

      localStorage.setItem("userId", data.value);
      localStorage.setItem("backOfficerId", data.entityId);

      dispatch(setRefreshToken(data.refreshToken));
      dispatch(setIsa(data.isSuperAdmin ? "true" : "false"));

      await dispatch(setHeaders(data.token)).unwrap();

      dispatch(getModuleRightsData(data.entityId));
      dispatch(getTenantPackingSettings());
      dispatch(getTenantSettings());

      return data;
    } catch (error) {
      dispatch(setAppReady(true));
      return rejectWithValue(error.data);
    }
  },
);

export const doAdminLogin = createAsyncThunk(
  "auth/doAdminLogin",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setAppReady(false));
      dispatch(setIsUnauthorise(false));
      dispatch(setIsRefreshingToken(false));
      clearFailedQueue();
      const { data } = await axios.post(
        ADMIN_API_URL.VERIFICATION.LOGIN,
        payload,
      );

      localStorage.setItem("userId", data.value);
      localStorage.setItem("backOfficerId", data.entityId);

      dispatch(setRefreshToken(data.refreshToken));
      dispatch(setIsa(data.isSuperAdmin ? "true" : "false"));

      await dispatch(setHeaders(data.token)).unwrap();

      dispatch(getModuleRightsData(data.entityId));

      return data;
    } catch (error) {
      dispatch(setAppReady(true));
      return rejectWithValue(error.data);
    }
  },
);

// Refresh Token API call
export const refreshTokenApi = createAsyncThunk(
  "auth/refreshToken",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      let { token, isa } = getState().auth;
      const refreshToken = localStorage.getItem("refreshToken");
      const apiUrl =
        isa === "true"
          ? ADMIN_API_URL.VERIFICATION.REFRESH_TOKEN
          : API_URL.VERIFICATION.REFRESH_TOKEN;
      const payload = {
        token: token,
        refreshToken: refreshToken,
      };
      const { data } = await axios.post(apiUrl, payload);
      dispatch(setHeaders(data.token));
      dispatch(setRefreshToken(data.refreshToken));
      dispatch(getModuleRightsData(data.entityId));
      return data.token;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getModuleRightsData = createAsyncThunk(
  "auth/getModuleRightsData",
  async (backOfficerId, { dispatch, getState, rejectWithValue }) => {
    try {
      const { isa } = getState().auth;
      const apiUrl =
        isa === "true"
          ? ADMIN_API_URL.VERIFICATION.GET_MODULE_RIGHT
          : API_URL.VERIFICATION.GET_MODULE_RIGHT;
      const { data } = await axios.get(`${apiUrl}?entityId=${backOfficerId}`);
      return Array.isArray(data) ? data : data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const sendOTP = createAsyncThunk(
  "auth/sendOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.SEND_OTP}`,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const verifyOTP = createAsyncThunk(
  "auth/verifyOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams(payload);
      const queryString = params.toString();
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.VERIFY_OTP}?${queryString}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const doRegister = createAsyncThunk(
  "auth/doRegister",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.VERIFICATION.REGISTER}`,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { isa } = getState().auth;
      const apiUrl =
        isa === "true"
          ? ADMIN_API_URL.VERIFICATION.CHANGE_PASSWORD
          : API_URL.VERIFICATION.CHANGE_PASSWORD;
      const { data } = await axios.post(`${apiUrl}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const changeUserPassword = createAsyncThunk(
  "auth/changeUserPassword",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { isa } = getState().auth;
      const apiUrl =
        isa === "true"
          ? ADMIN_API_URL.VERIFICATION.CHANGE_USER_PASSWORD
          : API_URL.VERIFICATION.CHANGE_USER_PASSWORD;
      const response = await axios.post(`${apiUrl}`, payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(
        error?.data || error?.response?.data || error?.message || error
      );
    }
  },
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const { isa } = getState().auth;
      const apiUrl =
        isa === "true"
          ? ADMIN_API_URL.VERIFICATION.UPDATE_PROFILE
          : API_URL.VERIFICATION.UPDATE_PROFILE;
      const { data } = await axios.put(`${apiUrl}`, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error?.data || error?.response?.data || error?.message || error
      );
    }
  },
);

export const getUserProfileData = createAsyncThunk(
  "auth/me",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const { isa } = getState().auth;
    try {
      const params = new URLSearchParams(payload);
      const queryString = params.toString();
      const apiUrl =
        isa === "true"
          ? ADMIN_API_URL.VERIFICATION.USER_PROFILE_DATA
          : API_URL.VERIFICATION.USER_PROFILE_DATA;
      const { data } = await axios.get(`${apiUrl}?${queryString}`);
      dispatch(setUserProfileData(data.data));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  token: null,
  isUnauthorise: false,
  userId: null,
  backOfficerId: null,
  error: null,
  isRefreshing: false,
  registerEmail: null,
  userProfileData: null,
  moduleRightsData: null,
  isRestricted: false,
  isAppReady: false,
  isa: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
    },
    setRefreshToken: (state, action) => {
      localStorage.setItem("refreshToken", action.payload);
    },
    setIsUnauthorise: (state, action) => {
      state.isUnauthorise = action.payload;
    },
    setIsRefreshingToken: (state, action) => {
      state.isRefreshing = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    setBackOfficerId: (state, action) => {
      state.backOfficerId = action.payload;
    },
    setRegisterEmail: (state, action) => {
      state.registerEmail = action.payload;
    },
    setUserProfileData: (state, action) => {
      if (action.payload) {
        state.userProfileData = action.payload;
        if (action.payload.isOnboardingCompleted !== undefined) {
          localStorage.setItem(
            "isTenantVerified",
            action.payload.isOnboardingCompleted ? "true" : "false",
          );
        }
      }
    },
    setIsa: (state, action) => {
      state.isa = action.payload;
      localStorage.setItem("isa", action.payload === "true" ? "true" : "false");
    },
    setModuleRightData: (state, action) => {
      state.moduleRightsData = action.payload;
    },
    setIsRestricted: (state, action) => {
      state.isRestricted = action.payload;
    },
    setAppReady: (state, action) => {
      state.isAppReady = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doLogin.fulfilled, (state, action) => {})
      .addCase(doLogin.rejected, (state, action) => {
        state.token = null;
        state.error = action;
      })
      .addCase(getModuleRightsData.fulfilled, (state, action) => {
        const normalizedPermissions = {};
        if (action.payload && Array.isArray(action.payload)) {
          action.payload.forEach(({ code, rightCode }) => {
            normalizedPermissions[code] = [...new Set(rightCode)];
          });
        }
        state.moduleRightsData = normalizedPermissions;
        setLocalStorageData("moduleRights", normalizedPermissions);
      })
      .addCase(getModuleRightsData.rejected, (state, action) => {
        state.moduleRightsData = {};
      });
  },
});

export const {
  setToken,
  setRefreshToken,
  setIsUnauthorise,
  setIsRefreshingToken,
  setUserId,
  setBackOfficerId,
  setRegisterEmail,
  setUserProfileData,
  setIsa,
  setModuleRightData,
  setIsRestricted,
  setAppReady,
} = authSlice.actions;
export default authSlice.reducer;
